import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faChevronDown, faChevronRight, faCircleArrowDown } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Fragment, ReactNode, useState } from 'react';
import { Button } from 'react-bootstrap';
import Markdown from 'markdown-to-jsx';

import ExpandableSection from '@components/ExpandableSection/ExpandableSection';
import Placeholder from '@components/Placeholder/Placeholder';
import { Requisition } from '@customTypes/requisition';
import { formatDateForDisplay } from '@helpers/date';
import { useDownload } from '@hooks/useDownload';
import {
  fetchOrderReportDetail,
  OrderReportDetail,
  reportDetailIsEmpty,
  ReportPhenotype,
  ReportVariant,
} from '@helpers/orderReportDetail';
import {
  getPatientResultsSummaryRows,
  PatientResultsSummaryGroup,
  PatientResultsSummaryRow,
} from '@helpers/patientResultsSummary';
import { IS_NATERA } from '@constants/constants';

import './PatientDetailResultsSummary.scss';

interface PatientDetailResultsSummaryProps {
  requisitions: Requisition[];
}

type FindingTone = 'alert' | 'caution' | 'clear' | 'quiet' | 'neutral';

const FINDING_LABELS: Record<string, string> = {
  POSITIVE: 'Positive',
  NEGATIVE: 'Negative',
  INCREASED_RISK: 'Increased risk',
  ELEVATED_RISK: 'Elevated risk',
  HIGH_RISK: 'High risk',
  INTERMEDIATE_RISK: 'Intermediate risk',
  AVERAGE_RISK: 'Average risk',
  BORDERLINE_RISK: 'Borderline risk',
  LOW_RISK: 'Low risk',
  MINIMAL_IMPACT: 'Minimal impact',
  MAJOR_IMPACT: 'Major impact',
  ALTERED_DOSE: 'Altered dose',
  CONTRAINDICATED: 'Contraindicated',
  UNCERTAIN: 'Uncertain',
  DECREASED_RISK: 'Decreased risk',
  NOT_INCREASED_RISK: 'Not increased risk',
  NOT_REPORTABLE: 'Not reportable',
  NOT_ANALYZED: 'Not analyzed',
};

const FINDING_TONES: Record<string, FindingTone> = {
  POSITIVE: 'alert',
  HIGH_RISK: 'alert',
  CONTRAINDICATED: 'alert',
  MAJOR_IMPACT: 'alert',
  INCREASED_RISK: 'caution',
  UNCERTAIN: 'caution',
  ELEVATED_RISK: 'caution',
  INTERMEDIATE_RISK: 'caution',
  ALTERED_DOSE: 'caution',
  NEGATIVE: 'clear',
  NOT_INCREASED_RISK: 'quiet',
  DECREASED_RISK: 'quiet',
  AVERAGE_RISK: 'quiet',
  LOW_RISK: 'quiet',
  BORDERLINE_RISK: 'quiet',
  MINIMAL_IMPACT: 'quiet',
  NOT_REPORTABLE: 'neutral',
  NOT_ANALYZED: 'neutral',
};

const findingLabel = (finding: string): string =>
  FINDING_LABELS[finding] ?? `${finding.charAt(0)}${finding.slice(1).toLowerCase().replace(/_/g, ' ')}`;

const FindingBadge = ({ label, tone }: { label: string; tone: FindingTone }) => (
  <span className={`badge rounded-pill results-summary-pill results-summary-pill--${tone}`}>{label}</span>
);

const ReportLink = ({ href, children }: { href?: string; children?: ReactNode }) => (
  <a href={href} target='_blank' rel='noopener noreferrer'>
    {children}
  </a>
);

const ReportText = ({ text }: { text: string }) => (
  <Markdown options={{ forceInline: true, overrides: { a: { component: ReportLink } } }}>{text}</Markdown>
);

const DetailField = ({ label, value, markdown = false }: { label: string; value: string; markdown?: boolean }) => {
  if (!value) {
    return null;
  }

  return (
    <p className='mb-1'>
      <span className='results-summary-detail-label'>{label}</span> {markdown ? <ReportText text={value} /> : value}
    </p>
  );
};

const VariantList = ({ variants }: { variants: ReportVariant[] }) => (
  <div className='results-summary-variants'>
    {variants.map(variant => (
      <div key={`${variant.gene}-${variant.variant}`} className='results-summary-variant'>
        <DetailField label='Gene' value={variant.gene} />
        <DetailField label='Result' value={variant.classification} />
        <DetailField label='Variant' value={variant.variant} />
        <DetailField label='Zygosity' value={variant.zygosity} />
        <DetailField label='Condition' value={variant.condition} markdown />
      </div>
    ))}
  </div>
);

const PhenotypeList = ({ phenotypes }: { phenotypes: ReportPhenotype[] }) => (
  <ul className='mb-0'>
    {phenotypes.map(item => (
      <li key={item.gene}>
        <span className='fw-semibold'>{item.gene}</span>
        {item.diplotype ? ` ${item.diplotype}` : ''}
        {`, ${item.phenotype}`}
      </li>
    ))}
  </ul>
);

const ReportDetailSkeleton = () => (
  <div className='results-summary-skeleton' role='status' aria-label='Loading report details'>
    <Placeholder size='xs' />
    <Placeholder size='sm' />
    <Placeholder size='sm' />
    <Placeholder size='sm' />
  </div>
);

const ReportDetailBody = ({ detail, glanceDetail }: { detail: OrderReportDetail; glanceDetail: string | null }) => {
  if (reportDetailIsEmpty(detail)) {
    return <p className='mb-0'>No additional detail in this report.</p>;
  }

  const summary = detail.clinicalSummary && detail.clinicalSummary !== glanceDetail ? detail.clinicalSummary : null;

  return (
    <div className='d-flex flex-column gap-3'>
      {summary && <p className='mb-0'>{summary}</p>}
      {detail.variants.length > 0 && <VariantList variants={detail.variants} />}
      {detail.phenotypes.length > 0 && <PhenotypeList phenotypes={detail.phenotypes} />}
      {detail.nextSteps.length > 0 && (
        <div>
          <p className='results-summary-detail-label mb-1'>Next steps</p>
          <ul className='mb-0'>
            {detail.nextSteps.map(step => (
              <li key={step}>
                <ReportText text={step} />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const GROUPS: { group: PatientResultsSummaryGroup; title: string }[] = [
  { group: 'panel', title: 'Panel' },
  { group: 'rareDisease', title: 'Rare disease' },
  { group: 'polygenic', title: 'Polygenic risk scores' },
  { group: 'medication', title: 'Medication response' },
];

const ResultSummaryRows = ({
  row,
  onDownloadReport,
}: {
  row: PatientResultsSummaryRow;
  onDownloadReport: (orderId: string) => void;
}) => {
  const [expanded, setExpanded] = useState(false);
  const [detail, setDetail] = useState<OrderReportDetail | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const reportedOn = formatDateForDisplay(row.reportedAt);
  const fullDetail = row.glanceDetail;
  const showFullDetail = Boolean(
    fullDetail && !row.isMedicationResponse && !detail?.clinicalSummary?.includes(fullDetail),
  );
  const showPgxLink = row.isMedicationResponse && !IS_NATERA;

  const scrollToPgxGuidance = () => {
    document.getElementById('pgxGuidance')?.scrollIntoView({ behavior: 'smooth' });
  };

  const onToggle = async () => {
    const nextExpanded = !expanded;
    setExpanded(nextExpanded);
    if (!nextExpanded || detail || isLoading) {
      return;
    }

    setIsLoading(true);
    setHasError(false);
    try {
      setDetail(await fetchOrderReportDetail(row.orderId));
    } catch {
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <tr className={expanded ? 'results-summary-row--expanded' : undefined}>
        <th scope='row' className='results-summary-name'>
          {row.testHeadline}
        </th>
        <td className='results-summary-result'>
          {row.finding && (
            <FindingBadge label={findingLabel(row.finding)} tone={FINDING_TONES[row.finding] ?? 'neutral'} />
          )}
        </td>
        <td className='results-summary-date'>{reportedOn}</td>
        <td className='results-summary-toggle'>
          <Button
            variant='link'
            className='results-summary-link'
            onClick={onToggle}
            aria-expanded={expanded}
            aria-label={`${expanded ? 'Hide details' : 'View details'} for ${row.testHeadline}`}
          >
            <FontAwesomeIcon icon={(expanded ? faChevronDown : faChevronRight) as IconDefinition} />
          </Button>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={4} className='results-summary-detail'>
            <div className='results-summary-detail-panel'>
              {showFullDetail && <p className='mb-2'>{fullDetail}</p>}
              {isLoading && <ReportDetailSkeleton />}
              {hasError && <p className='mb-0'>Could not load this report.</p>}
              {detail && <ReportDetailBody detail={detail} glanceDetail={row.glanceDetail} />}
              <Button
                variant='link'
                className='results-summary-report-disclaimer'
                onClick={() => onDownloadReport(row.orderId)}
                aria-label={`Download the report to see all details for ${row.testHeadline}`}
              >
                <FontAwesomeIcon icon={faCircleArrowDown as IconDefinition} />
                Download the report to see all details
              </Button>
              {showPgxLink && (
                <Button variant='link' className='results-summary-pgx-link' onClick={scrollToPgxGuidance}>
                  Explore medication guidelines
                </Button>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

const findReportDownload = (requisitions: Requisition[], orderId: string) => {
  for (const requisition of requisitions) {
    const order = requisition.orders.find(item => item.order_id === orderId);
    if (order) {
      return { order, requisitionId: requisition.requisition_id };
    }
  }

  return null;
};

export const PatientDetailResultsSummary: React.FC<PatientDetailResultsSummaryProps> = ({ requisitions }) => {
  const { onDownload } = useDownload();
  const rows = getPatientResultsSummaryRows(requisitions);

  const onDownloadReport = (orderId: string) => {
    const match = findReportDownload(requisitions, orderId);
    if (!match) {
      return;
    }

    onDownload(match.order, match.requisitionId);
  };

  if (!rows.length) {
    return null;
  }

  return (
    <ExpandableSection
      className='patient-results-summary'
      id='resultsSummary'
      expandableContentId='resultsSummaryContent'
      headerTitle='Results Summary'
      defaultIsCollapsed
    >
      <table className='table results-summary-table mb-0'>
        <caption className='visually-hidden'>Results summary</caption>
        <thead>
          <tr>
            <th scope='col'>Test</th>
            <th scope='col'>Result</th>
            <th scope='col'>Reported</th>
            <th scope='col'>
              <span className='visually-hidden'>More</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {GROUPS.map(({ group, title }) => {
            const groupRows = rows.filter(row => row.group === group);
            if (!groupRows.length) {
              return null;
            }

            return (
              <Fragment key={group}>
                <tr className='results-summary-group-row'>
                  <th colSpan={4}>{title}</th>
                </tr>
                {groupRows.map(row => (
                  <ResultSummaryRows key={row.orderId} row={row} onDownloadReport={onDownloadReport} />
                ))}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </ExpandableSection>
  );
};
